import { useEffect, useMemo, useState } from "react";
import { EditIcon } from "../../../assets/icons";
import LayoutWrapper from "../../../layouts/landing-page-layout-wrapper/layoutWrapper";
import { useLazyGetDeliveryAddressQuery } from "../../../service/delivery-address-service";
import AddressRadio from "./address-radio";
import styles from "./billinginfo.module.scss";
import { toast } from "react-toastify";
import { DeliveryAddress } from "../../../model/delivery-address";
import { BounceLoader } from "react-spinners";
import AddressForm from "./address-form";
import ConfirmBillOrder from "./confirm-order";
import { useDispatch, useSelector } from "react-redux";
import { newBuySolarProductDetailsSelector } from "../../../stores/slices/buy-solar-product-slice";
import { USER_BILL_TYPE } from "./type";
import { deleteGuestDeliveryAddress, guestDeliveryAddressSelector } from "../../../stores/slices/guest-delivery-address-slice";
import PaymentOptions, { PaymentOption } from "../../../common/components/payment-options/PaymentOptions";
import { CustomerPaystackCard } from "../../../model/customer/customer";
import { useCreateAnGuestOrderMutation, useCreateAnOrderMutation } from "../../../service/order-service";
import { ApplicationRoutes } from "../../../routes";
import { TransactionType } from "../../../model/transaction";
import { isCustomer, isGuest } from "../../../guards/common/authentification-functions";
import { useLocation, useNavigate } from "react-router-dom";
import { showLoader, hideLoader } from "../../../stores/slices/loader-slice";
import { AppDispatch } from "../../../stores/store";
import { useLazyGetCustomerInfoQuery } from "../../../service/customer-service";

const BillingInfoPage = () => {
	const newBuySolarProductDetails = useSelector(newBuySolarProductDetailsSelector);

	const guestDeliveryAddress = useSelector(guestDeliveryAddressSelector);

	const location = useLocation();

	const USERTYPE = (() => {
		if (isCustomer()) return USER_BILL_TYPE.CUSTOMER;

		if (isGuest()) return USER_BILL_TYPE.GUEST;

		return USER_BILL_TYPE.GUEST;
	})();

	const navigate = useNavigate();

	const dispatch = useDispatch<AppDispatch>();

	const [address, setAddress] = useState<Array<DeliveryAddress>>([]);
	const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null);
	const [confirmedAddressId, setConfirmedAddressId] = useState<string | null>(null);
	const [getDeliveryAddress, { isLoading: loadingDeliveryAddresses, isFetching, data }] = useLazyGetDeliveryAddressQuery();
	const [showAddressDialog, setShowAddressDialog] = useState(false);
	const [addressToEdit, setAddressToEdit] = useState<DeliveryAddress>();

	const [selectedOption, setSelectedOption] = useState<PaymentOption | undefined>();
	const [selectedCardId, setSelectedCardId] = useState<CustomerPaystackCard | undefined>(undefined);

	const [createAnGuestOrder] = useCreateAnGuestOrderMutation();
	const [createAnOrder] = useCreateAnOrderMutation();

	const [getCustomerInfo, _] = useLazyGetCustomerInfoQuery();

	const isInstallment = useMemo(() => !!newBuySolarProductDetails?.durationOfPaymentsInMonths, [newBuySolarProductDetails]);

	useEffect(() => {
		if (isCustomer()) {
			getDeliveryAddress()
				.unwrap()
				.then(res => setAddress(res))
				.catch(err => toast.error(err?.data?.title as string));
		}
		if (isGuest()) {
			setAddress(guestDeliveryAddress);
		}
	}, [guestDeliveryAddress]);

	useEffect(() => {
		if (isCustomer()) {
			setAddress(data ?? []);
		}
	}, [data]);

	const handleSelectAddress = (id: string) => {
		setSelectedAddressId(id);
	};

	const handleSetConfirmedAddress = () => {
		if (confirmedAddressId) {
			setConfirmedAddressId(null);
			return;
		}
		if (!selectedAddressId) {
			toast.warning("You need to select an address");
			return;
		}
		setConfirmedAddressId(selectedAddressId);
	};

	const handleEditAddress = (address: DeliveryAddress) => {
		setShowAddressDialog(true);
		setAddressToEdit(address);
	};

	const handleCloseAddresDialog = () => {
		setShowAddressDialog(false);
		setAddressToEdit(undefined);
	};

	const getInstallAddress = (deliveryAddress: DeliveryAddress) => {
		const selectedAddress = address.find(x => x.id === selectedAddressId);

		let result = "";

		if (selectedAddress?.deliveryAddressInformation?.address) {
			result += selectedAddress?.deliveryAddressInformation?.address;
		}
		if (selectedAddress?.deliveryAddressInformation?.city) {
			result += `, ${selectedAddress?.deliveryAddressInformation?.city}`;
		}
		if (selectedAddress?.deliveryAddressInformation?.region) {
			result += `, ${selectedAddress?.deliveryAddressInformation?.region}`;
		}
		if (selectedAddress?.deliveryAddressInformation?.zip) {
			result += `, ${selectedAddress?.deliveryAddressInformation?.zip}`;
		}

		return result;
	};

	const onConfirmOrder = async () => {
		if (!selectedAddressId) {
			toast.warning("You need to select an address");
			return;
		}

		if (isGuest()) {
			handleGuestCreateOrder();
			return;
		}

		if (isCustomer()) {
			if (isInstallment) {
				handleInstallmentCreateOrder();
				return;
			}

			handleCustomerCreateOrder();
		}
	};

	const handleGuestCreateOrder = async () => {
		if (!selectedOption) {
			toast.warning("You need to select a payment method");
			return;
		}

		const selectedAddress = guestDeliveryAddress.find(x => x.id === selectedAddressId);

		const installAddress = getInstallAddress(selectedAddress!);

		try {
			dispatch(showLoader());

			const result = await createAnGuestOrder({
				productId: newBuySolarProductDetails?.productDetailsId!,
				firstName: selectedAddress?.deliveryAddressInformation.firstName!,
				lastName: selectedAddress?.deliveryAddressInformation?.lastName!,
				phoneNumber: selectedAddress?.deliveryAddressInformation?.phoneNumber!,
				email: selectedAddress?.deliveryAddressInformation?.email ?? guestDeliveryAddress[0].deliveryAddressInformation?.email!,
				additionalInformation: selectedAddress?.deliveryAddressInformation?.additionalInformation!,
				installationAddress: installAddress,
				additionalPhoneNumber: selectedAddress?.deliveryAddressInformation?.additionalPhoneNumber!,
				numberOfFloors: newBuySolarProductDetails?.numberOfFloors,
				roofType: newBuySolarProductDetails?.roofTypeId,
				redirectUrl: `${window.location.origin}/${ApplicationRoutes.CONFIRM_PAYMENT}`,
				failedRedirectUrl: `${window.location.origin}/${ApplicationRoutes.BILLING_INFO}?failed=true`,
			}).unwrap();
             dispatch(deleteGuestDeliveryAddress())
			window.location.href = result?.authorizationUrl;
		} catch (error: any) {
			toast.error(error.data?.title || "Something went wrong. Please try again.");
		} finally {
			dispatch(hideLoader());
		}
	};
	const handleCustomerCreateOrder = async () => {
		if (!selectedOption) {
			toast.warning("You need to select a payment method");
			return;
		}
		if (selectedOption === "card" && !selectedCardId) {
			toast.warning("You need to select a card");
			return;
		}

		const paymentType: TransactionType = selectedOption === "card" ? "Payment Hub" : "Cash Payment";

		dispatch(showLoader());

		createAnOrder({
			productId: newBuySolarProductDetails?.productDetailsId!,
			roofType: newBuySolarProductDetails?.roofTypeId,
			numberOfFloors: newBuySolarProductDetails?.numberOfFloors,
			paymentType: paymentType,
			deliveryAddressId: selectedAddressId!,
			paystackCardId: selectedCardId?.id,
		})
			.unwrap()
			.then(res => {
				dispatch(deleteGuestDeliveryAddress())
				navigate(`/${ApplicationRoutes.CONFIRM_PAYMENT}?order=${res.orderId}`);
			})
			.catch(err => {
				toast.error(err?.data?.title as string);
			})
			.finally(() => {
				dispatch(hideLoader());
			});
	};

	const handleInstallmentCreateOrder = async () => {
		dispatch(showLoader());
		
		try {
			const result = await getCustomerInfo().unwrap();
			if (result.monoVerified && !!result.address && !!result.companyName && !!result.idCardNumber) {
				const result = await createAnOrder({
					productId: newBuySolarProductDetails?.productDetailsId!,
					durationOfPaymentsInMonths: newBuySolarProductDetails?.durationOfPaymentsInMonths,
					roofType: newBuySolarProductDetails?.roofTypeId,
					numberOfFloors: newBuySolarProductDetails?.numberOfFloors,
					deliveryAddressId: selectedAddressId!,
					paystackCardId: selectedCardId?.id,
				}).unwrap();

				toast.success("Order created successfully");
				navigate(`/${ApplicationRoutes.CONFIRM_PAYMENT}?order=${result.orderId}`);
			}
			else {
				navigate(`${ApplicationRoutes.CUSTOMER_SIGNUP}`, { state: { orderUrl: location.pathname } });
			}
		} catch (error: any) {
			toast.error(error.data?.title || "Something went wrong. Please try again.");
		} finally {
			dispatch(hideLoader());
		}
	};

	return (
		<>
			<div className={styles.container}>
				<div className={styles.head}></div>
				<LayoutWrapper>
					<div className={styles.subjectRow}>
						<h2 className={styles.title}>Billing Information</h2>
						<p className={styles.subTitle}>Manage your billing information</p>
					</div>
					<div className={styles.innerContainer}>
						<div className={styles.rightSection}>
							<div className={styles.sectionContainer}>
								<h1 className={styles.sectionNumber}>1. Customer Address</h1>
								<div className={styles.section}>
									{data && <h2 className={styles.sectionHeader}>{address?.length == 0 ? "No address yet" : `Address Book(${address?.length})`}</h2>}
									{(loadingDeliveryAddresses || isFetching) && (
										<div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
											<BounceLoader color="#ff7926" title="Loading address" size={45} />
										</div>
									)}
									{address.length > 0 &&
										(!confirmedAddressId
											? address?.map((item, index) => {
													return (
														<AddressRadio isSelected={selectedAddressId === item?.id} onClick={() => handleSelectAddress(item?.id)} key={item?.id}>
															<div className={styles.addInfo}>
																<div className={styles.nameRow}>
																	<h4 className={styles.name}>
																		{item?.deliveryAddressInformation?.firstName} {item?.deliveryAddressInformation?.lastName}
																	</h4>
																	<button onClick={() => handleEditAddress(item)} className={styles.editButton}>
																		EDIT <EditIcon />
																	</button>
																</div>
																<p className={styles.address}>
																	{item?.deliveryAddressInformation?.address}, {item?.deliveryAddressInformation?.city},{" "}
																	{item?.deliveryAddressInformation?.region} | {item?.deliveryAddressInformation?.city} -{" "}
																	{item?.deliveryAddressInformation?.region}
																</p>
																<span className={styles.number}>{item?.deliveryAddressInformation?.phoneNumber}</span>
																{item.isDefault && <div className={styles.tag}>Default Address</div>}
															</div>
														</AddressRadio>
													);
											  })
											: address
													.filter(x => x.id === confirmedAddressId)
													.map((item, index) => {
														return (
															<AddressRadio isSelected={selectedAddressId === item?.id} onClick={() => handleSelectAddress(item?.id)} key={item?.id}>
																<div className={styles.addInfo}>
																	<div className={styles.nameRow}>
																		<h4 className={styles.name}>
																			{item?.deliveryAddressInformation?.firstName} {item?.deliveryAddressInformation?.lastName}
																		</h4>
																		<button className={styles.editButton}>
																			EDIT <EditIcon />
																		</button>
																	</div>
																	<p className={styles.address}>
																		{item?.deliveryAddressInformation?.address}, {item?.deliveryAddressInformation?.city},{" "}
																		{item?.deliveryAddressInformation?.region} | {item?.deliveryAddressInformation?.city} -{" "}
																		{item?.deliveryAddressInformation?.region}
																	</p>
																	<span className={styles.number}>{item?.deliveryAddressInformation?.phoneNumber}</span>
																	{item.isDefault && <div className={styles.tag}>Default Address</div>}
																</div>
															</AddressRadio>
														);
													}))}

									<button onClick={() => setShowAddressDialog(true)} className={styles.addBillButton}>
										+ Add Address
									</button>

									{address?.length > 0 && (
										<div className={styles.buttonSection}>
											<button onClick={handleSetConfirmedAddress} className={styles.cancelButton}>
												Cancel
											</button>
											<button onClick={handleSetConfirmedAddress} className={styles.selectButton}>
												Select Address
											</button>
										</div>
									)}
								</div>
							</div>
							{!isInstallment && (
								<div className={styles.sectionContainer}>
									<h1 className={styles.sectionNumber}>3. Payment Method</h1>
									<div className={styles.paymentContainer}>
										<PaymentOptions
											selectedOption={selectedOption}
											onSelectPaymentOption={option => {
												setSelectedOption(option);
											}}
											selectedCard={selectedCardId}
											onSelectedCard={card => {
												setSelectedCardId(card);
											}}
										/>
									</div>
								</div>
							)}
						</div>
						<div className={styles.leftSection}>
							<ConfirmBillOrder
								totalAmount={newBuySolarProductDetails?.totalAmount?.toLocaleString() ?? "-"}
								amount={newBuySolarProductDetails?.amount?.toLocaleString() ?? "-"}
								onConfirmOrder={onConfirmOrder}
							/>
						</div>
					</div>
				</LayoutWrapper>
			</div>
			{showAddressDialog && <AddressForm bilType={USERTYPE} handleCancel={handleCloseAddresDialog} isEdit={!!addressToEdit} addressToEdit={addressToEdit} />}
		</>
	);
};

export default BillingInfoPage;
